import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  HIGHLIGHT_ICONS,
  PRODUCT_BLOCK_LIMITS,
  PRODUCT_BLOCK_TYPES,
  tidyProductBlocks,
  type BilingualTextDto,
  type HighlightIcon,
  type ProductBlockInput,
  type ProductBlockType,
  type ProductDetailDto,
  type ProductHighlightInput,
  type ProductImageDto,
  type SpecificationDto,
} from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { fabricOfImage, fabricsOf } from '@/lib/fabrics';
import {
  SINGLE_BLOCK_TYPES,
  asPlain,
  blockSummary,
  blocksFromDescription,
  blocksPlainText,
  canAddBlock,
  duplicateBlock,
  emptyText,
  moveItem,
  newBlock,
  newBlockId,
  newHighlight,
  suggestIcon,
  type BlockOf,
  type PlainText,
} from '@/lib/description-blocks';
import { BilingualField, ToggleField, type BilingualValue } from '@/components/fields';
import {
  ArrowLeftIcon,
  CardsIcon,
  ChevronDownIcon,
  CopyIcon,
  EyeIcon,
  FileIcon,
  GripIcon,
  HighlightGlyph,
  ImageIcon,
  LayersIcon,
  LayoutIcon,
  ListIcon,
  PlusIcon,
  SparkleIcon,
  TextIcon,
  TrashIcon,
} from '@/components/icons';
import { Menu, useDismiss } from '@/components/menu';
import { Section, Segmented, Sheet } from '@/components/page';
import { SortableItem, SortableList } from '@/components/sortable';
import { Alert, Button, ConfirmDialog, IconButton, Select, cx } from '@/components/ui';

/**
 * The product page's long description, written in blocks (ADR-055).
 *
 * A product nobody has laid out is drawn by its theme from the plain
 * description, its photographs and its specifications; the editor says so and
 * keeps that description editable, one click from laying the page out. Once
 * laid out, the page is a column of typed blocks — text, photographs,
 * highlight cards, a list, the looks, the categories — each a card that folds,
 * dragged or stepped into order, and a preview reads it back the way a shopper
 * would, in either language.
 *
 * The blocks live in the product form's values, so nothing typed here is lost
 * on another tab, and they save with the product from its one bar.
 */

const L = PRODUCT_BLOCK_LIMITS;

type Language = 'ar' | 'en';

type IconComponent = (props: { className?: string }) => ReactNode;

const TYPE_ICONS: Record<ProductBlockType, IconComponent> = {
  text: TextIcon,
  photos: ImageIcon,
  highlights: CardsIcon,
  list: ListIcon,
  looks: SparkleIcon,
  categories: LayersIcon,
};

const isKnownType = (type: string): type is ProductBlockType => (PRODUCT_BLOCK_TYPES as readonly string[]).includes(type);

/** What every block's editor reads about the product around it. */
interface Context {
  product: ProductDetailDto | null;
  /** The product's own photographs, in its order: the only ones a block may show. */
  images: ProductImageDto[];
  /** Its answered specifications, resolved in the admin's language. */
  specifications: SpecificationDto[];
  /** The names of the other categories it is listed in — the Organization card's. */
  alsoIn: string[];
  onTab: (tab: 'media' | 'specs') => void;
}

export function DescriptionEditor({
  product,
  name,
  shortDescription,
  description,
  blocks,
  alsoIn,
  onDescription,
  onBlocks,
  onTab,
}: {
  /** Null while creating: photographs and specifications wait for a saved product. */
  product: ProductDetailDto | null;
  name: BilingualValue;
  shortDescription: BilingualValue;
  /** The plain description — what the theme lays out while there are no blocks. */
  description: BilingualValue;
  /** Null is the theme's standard layout. */
  blocks: ProductBlockInput[] | null;
  alsoIn: string[];
  onDescription: (next: BilingualValue) => void;
  onBlocks: (next: ProductBlockInput[] | null) => void;
  onTab: (tab: 'media' | 'specs') => void;
}) {
  const images = useMemo(() => (product ? [...product.images].sort((a, b) => a.sortOrder - b.sortOrder) : []), [product]);
  const ctx: Context = { product, images, specifications: product?.specifications ?? [], alsoIn, onTab };
  // One card open at a time; a page of a single block — a new product's — opens on it.
  const [openId, setOpenId] = useState<string | null>(() => (blocks?.length === 1 ? blocks[0].id : null));

  if (blocks === null) {
    return (
      <StandardLayout
        description={description}
        onDescription={onDescription}
        onLayOut={() => {
          const next = blocksFromDescription(description);
          onBlocks(next);
          setOpenId(next[0].id);
        }}
      />
    );
  }

  return (
    <BlocksEditor
      ctx={ctx}
      name={name}
      shortDescription={shortDescription}
      blocks={blocks}
      openId={openId}
      onOpen={setOpenId}
      onBlocks={onBlocks}
      onStandard={
        product
          ? () => {
              // The words stay as the plain description, as the API would have derived them.
              onDescription(blocksPlainText(blocks));
              onBlocks(null);
              setOpenId(null);
            }
          : undefined
      }
    />
  );
}

// ─── The theme's standard layout ─────────────────────────────────────────────

function StandardLayout({
  description,
  onDescription,
  onLayOut,
}: {
  description: BilingualValue;
  onDescription: (next: BilingualValue) => void;
  onLayOut: () => void;
}) {
  const t = useT();

  return (
    <Section
      title={t('descStandardTitle')}
      subtitle={t('descStandardBody')}
      action={
        <Button size="sm" onClick={onLayOut}>
          <LayoutIcon className="size-4" />
          {t('descLayOut')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <BilingualField label={t('productDescription')} value={description} onChange={onDescription} multiline rows={8} />
        <div className="flex items-start gap-3 rounded-control border border-dashed border-line bg-surface-sunken/60 p-3">
          <LayoutIcon className="mt-0.5 size-4 shrink-0 text-ink-muted" />
          <p className="text-xs text-ink-muted">{t('descLayOutHint')}</p>
        </div>
      </div>
    </Section>
  );
}

// ─── The blocks ──────────────────────────────────────────────────────────────

function BlocksEditor({
  ctx,
  name,
  shortDescription,
  blocks,
  openId,
  onOpen,
  onBlocks,
  onStandard,
}: {
  ctx: Context;
  name: BilingualValue;
  shortDescription: BilingualValue;
  blocks: ProductBlockInput[];
  openId: string | null;
  onOpen: (id: string | null) => void;
  onBlocks: (next: ProductBlockInput[]) => void;
  /** Offered only for a saved product: a new one has no standard layout to go back to yet. */
  onStandard?: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [revealId, setRevealId] = useState<string | null>(null);
  // The last block removed, one step back — cheaper than a confirmation on every remove.
  const [removed, setRemoved] = useState<{ block: ProductBlockInput; index: number } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmStandard, setConfirmStandard] = useState(false);

  const typeLabel = (type: string) => (isKnownType(type) ? t(`blockType_${type}`) : t('blockTypeUnknown'));
  const specificationLabel = (code: string) => ctx.specifications.find((spec) => spec.code === code)?.label;

  const show = (id: string) => {
    onOpen(id);
    setRevealId(id);
  };

  const add = (type: ProductBlockType) => {
    // A card starts from the first specification when there is one: what Rtex's cards always read.
    const first = ctx.specifications[0];
    const block: ProductBlockInput =
      type === 'highlights' && first
        ? { id: newBlockId(), type, items: [newHighlight(first.code, suggestIcon(first.code))] }
        : newBlock(type);
    onBlocks([...blocks, block]);
    setRemoved(null);
    show(block.id);
  };

  const remove = (index: number) => {
    setRemoved({ block: blocks[index], index });
    onBlocks(blocks.filter((_, position) => position !== index));
  };

  const undo = () => {
    if (!removed) return;
    const next = [...blocks];
    next.splice(Math.min(removed.index, next.length), 0, removed.block);
    onBlocks(next);
    setRemoved(null);
    show(removed.block.id);
  };

  const duplicate = (index: number) => {
    const next = duplicateBlock(blocks, index);
    if (next.length === blocks.length) return;
    onBlocks(next);
    show(next[index + 1].id);
  };

  return (
    <>
      <Section
        title={t('descBlocksTitle')}
        subtitle={t('descBlocksSubtitle', { count: count(blocks.length, locale), max: count(L.blocks, locale) })}
        action={
          <>
            <Button variant="secondary" size="sm" onClick={() => setPreviewing(true)}>
              <EyeIcon className="size-4" />
              {t('descPreview')}
            </Button>
            {onStandard ? (
              <Menu
                label={t('descMoreActions')}
                bordered
                items={[{ label: t('descUseStandard'), icon: <LayoutIcon className="size-4" />, onSelect: () => setConfirmStandard(true) }]}
              />
            ) : null}
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {blocks.length === 0 ? (
            <div className="rounded-control border-2 border-dashed border-line px-4 py-8 text-center">
              <p className="text-sm font-medium text-ink">{t('descNoBlocks')}</p>
              <p className="mt-1 text-xs text-ink-muted">{t('descNoBlocksHint')}</p>
            </div>
          ) : (
            <SortableList items={blocks} className="flex flex-col gap-2" onReorder={onBlocks}>
              {(block, index) => (
                <SortableItem key={block.id} id={block.id}>
                  {(handle) => (
                    <BlockCard
                      block={block}
                      index={index}
                      total={blocks.length}
                      open={openId === block.id}
                      reveal={revealId === block.id}
                      label={typeLabel(block.type)}
                      handle={handle}
                      ctx={ctx}
                      canDuplicate={canAddBlock(blocks, block.type)}
                      specificationLabel={specificationLabel}
                      onToggle={() => onOpen(openId === block.id ? null : block.id)}
                      onChange={(next) => onBlocks(blocks.map((entry, position) => (position === index ? next : entry)))}
                      onMove={(delta) => onBlocks(moveItem(blocks, index, index + delta))}
                      onDuplicate={() => duplicate(index)}
                      onRemove={() => remove(index)}
                    />
                  )}
                </SortableItem>
              )}
            </SortableList>
          )}

          {removed && canAddBlock(blocks, removed.block.type) ? (
            <div role="status" className="flex items-center justify-between gap-3 rounded-control border border-line bg-surface-sunken px-3 py-1.5 text-sm text-ink-muted">
              <span className="min-w-0 truncate">{t('blockRemoved', { type: typeLabel(removed.block.type) })}</span>
              <Button variant="ghost" size="sm" onClick={undo}>
                {t('undo')}
              </Button>
            </div>
          ) : null}

          <AddBlockBar blocks={blocks} onAdd={add} />
        </div>
      </Section>

      {previewing ? (
        <DescriptionPreview ctx={ctx} name={name} shortDescription={shortDescription} blocks={blocks} onClose={() => setPreviewing(false)} />
      ) : null}

      <ConfirmDialog
        open={confirmStandard}
        title={t('descUseStandardTitle')}
        body={t('descUseStandardBody')}
        confirmLabel={t('descUseStandardConfirm')}
        cancelLabel={t('cancel')}
        onConfirm={() => {
          setConfirmStandard(false);
          onStandard?.();
        }}
        onCancel={() => setConfirmStandard(false)}
      />
    </>
  );
}

/** A button per type, each saying what it shows; looks and categories once each. */
function AddBlockBar({ blocks, onAdd }: { blocks: ProductBlockInput[]; onAdd: (type: ProductBlockType) => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const full = blocks.length >= L.blocks;

  return (
    <div className="border-t border-line pt-4">
      <p className="mb-2 text-sm font-medium text-ink">{t('addBlock')}</p>
      <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-3">
        {PRODUCT_BLOCK_TYPES.map((type) => {
          const Icon = TYPE_ICONS[type];
          const once = SINGLE_BLOCK_TYPES.includes(type) && blocks.some((block) => block.type === type);
          return (
            <button
              key={type}
              type="button"
              disabled={!canAddBlock(blocks, type)}
              onClick={() => onAdd(type)}
              className="group flex items-start gap-3 rounded-control border border-line bg-surface p-3 text-start transition-colors enabled:hover:border-brand-300 enabled:hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-55"
            >
              <span
                aria-hidden
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-chip bg-surface-sunken text-ink-muted transition-colors group-enabled:group-hover:bg-brand-50 group-enabled:group-hover:text-brand-700"
              >
                <Icon className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1 text-sm font-medium text-ink">
                  <PlusIcon className="size-3.5 text-ink-faint" />
                  {t(`blockType_${type}`)}
                </span>
                <span className="block text-xs text-ink-muted">{once ? t('addBlockOnce') : t(`blockTypeHint_${type}`)}</span>
              </span>
            </button>
          );
        })}
      </div>
      {full ? <p className="mt-2 text-xs text-ink-muted">{t('addBlockFull', { max: count(L.blocks, locale) })}</p> : null}
    </div>
  );
}

/**
 * One block: a header that says what it is and — folded — what it holds, and
 * under it, when open, that type's own editor. Dragged by its handle, or moved
 * a step at a time by its arrows, which a keyboard and a screen reader reach.
 */
function BlockCard({
  block,
  index,
  total,
  open,
  reveal,
  label,
  handle,
  ctx,
  canDuplicate,
  specificationLabel,
  onToggle,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
}: {
  block: ProductBlockInput;
  index: number;
  total: number;
  open: boolean;
  /** Just added: scrolled to, and its first field focused. */
  reveal: boolean;
  label: string;
  handle: { attributes: Record<string, unknown>; listeners: Record<string, unknown> };
  ctx: Context;
  canDuplicate: boolean;
  specificationLabel: (code: string) => string | undefined;
  onToggle: () => void;
  onChange: (next: ProductBlockInput) => void;
  onMove: (delta: -1 | 1) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const card = useRef<HTMLDivElement>(null);
  const bodyId = useId();
  const known = isKnownType(block.type);
  const Icon: IconComponent = known ? TYPE_ICONS[block.type] : FileIcon;

  useEffect(() => {
    if (!reveal || !card.current) return;
    card.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    card.current.querySelector<HTMLElement>('[data-block-body] input, [data-block-body] textarea')?.focus({ preventScroll: true });
  }, [reveal]);

  const summary = blockSummary(block, locale, specificationLabel);
  const counted =
    summary.count === null
      ? null
      : block.type === 'photos'
        ? t('blockPhotosCount', { count: count(summary.count, locale) })
        : t('blockPointsCount', { count: count(summary.count, locale) });
  const automatic = block.type === 'looks' || block.type === 'categories';
  const folded =
    [summary.text, counted].filter(Boolean).join(' · ') ||
    (automatic ? t(`blockTypeHint_${block.type}`) : known ? t('blockNothingYet') : t('blockTypeUnknownHint'));

  return (
    <div
      ref={card}
      className={cx('rounded-control border bg-surface transition-colors', open ? 'border-brand-300 shadow-card' : 'border-line')}
    >
      <div className="flex items-center gap-1.5 px-2 py-2">
        <span
          {...handle.attributes}
          {...handle.listeners}
          aria-label={t('dragToReorder')}
          className="inline-flex size-8 shrink-0 cursor-grab items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
        >
          <GripIcon className="size-4" />
        </span>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-control py-0.5 text-start"
        >
          <span
            aria-hidden
            className={cx(
              'inline-flex size-8 shrink-0 items-center justify-center rounded-chip transition-colors',
              open ? 'bg-brand-50 text-brand-700' : 'bg-surface-sunken text-ink-muted',
            )}
          >
            <Icon className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium text-ink">{label}</span>
            <span className={cx('block truncate text-xs', summary.text || counted ? 'text-ink-muted' : 'text-ink-faint')}>
              {open && known ? t(`blockTypeHint_${block.type as ProductBlockType}`) : folded}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center">
          <IconButton
            label={t('moveUp')}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            className="size-8 border-0 disabled:pointer-events-none disabled:opacity-35"
          >
            <ArrowLeftIcon className="size-4 rotate-90" />
          </IconButton>
          <IconButton
            label={t('moveDown')}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            className="size-8 border-0 disabled:pointer-events-none disabled:opacity-35"
          >
            <ArrowLeftIcon className="size-4 -rotate-90" />
          </IconButton>
          <Menu
            label={t('blockActions')}
            items={[
              { label: t('duplicate'), icon: <CopyIcon className="size-4" />, disabled: !canDuplicate, onSelect: onDuplicate },
              { label: t('remove'), icon: <TrashIcon className="size-4" />, danger: true, onSelect: onRemove },
            ]}
          />
          <IconButton
            label={open ? t('done') : t('editContent')}
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={onToggle}
            className="size-8 border-0"
          >
            <ChevronDownIcon className={cx('size-4 transition-transform', open && 'rotate-180')} />
          </IconButton>
        </div>
      </div>

      {open ? (
        <div id={bodyId} data-block-body className="rounded-b-control border-t border-line bg-surface-sunken/50 p-4">
          <BlockBody block={block} ctx={ctx} onChange={onChange} />
        </div>
      ) : null}
    </div>
  );
}

function BlockBody({ block, ctx, onChange }: { block: ProductBlockInput; ctx: Context; onChange: (next: ProductBlockInput) => void }) {
  const t = useT();
  switch (block.type) {
    case 'text':
      return <TextBody block={block} onChange={onChange} />;
    case 'photos':
      return <PhotosBody block={block} ctx={ctx} onChange={onChange} />;
    case 'highlights':
      return <HighlightsBody block={block} ctx={ctx} onChange={onChange} />;
    case 'list':
      return <ListBody block={block} onChange={onChange} />;
    case 'looks':
    case 'categories':
      return <AutomaticBody block={block} ctx={ctx} onChange={onChange} />;
    default:
      return <p className="text-sm text-ink-muted">{t('blockTypeUnknownHint')}</p>;
  }
}

// ─── Text ────────────────────────────────────────────────────────────────────

function TextBody({ block, onChange }: { block: BlockOf<'text'>; onChange: (next: BlockOf<'text'>) => void }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-4">
      <BilingualField
        label={t('blockHeading')}
        value={asPlain(block.heading)}
        limit={L.heading}
        onChange={(heading) => onChange({ ...block, heading })}
      />
      <BilingualField
        label={t('blockBody')}
        hint={t('blockBodyHint')}
        value={asPlain(block.body)}
        limit={L.body}
        multiline
        rows={6}
        onChange={(body) => onChange({ ...block, body })}
      />
    </div>
  );
}

// ─── Photos ──────────────────────────────────────────────────────────────────

function PhotosBody({
  block,
  ctx,
  onChange,
}: {
  block: BlockOf<'photos'>;
  ctx: Context;
  onChange: (next: BlockOf<'photos'>) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [picking, setPicking] = useState(false);
  const byId = new Map(ctx.images.map((image) => [image.id, image]));
  const wide = block.photos.length === 1;

  // A new product's photographs are uploads without ids yet; blocks point at ids.
  if (!ctx.product || (ctx.images.length === 0 && block.photos.length === 0)) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-control border-2 border-dashed border-line bg-surface px-4 py-8 text-center">
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
          <ImageIcon className="size-5" />
        </span>
        <p className="text-sm font-medium text-ink">{ctx.product ? t('blockPhotosNone') : t('blockPhotosSaveFirst')}</p>
        <p className="max-w-sm text-xs text-ink-muted">{ctx.product ? t('blockPhotosNoneHint') : t('blockPhotosSaveFirstHint')}</p>
        <Button variant="secondary" size="sm" onClick={() => ctx.onTab('media')}>
          {t('goToMedia')}
        </Button>
      </div>
    );
  }

  const setPhoto = (index: number, patch: Partial<BlockOf<'photos'>['photos'][number]>) =>
    onChange({ ...block, photos: block.photos.map((photo, position) => (position === index ? { ...photo, ...patch } : photo)) });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ink-muted">{t('blockPhotosHint')}</p>
        {block.photos.length > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
            <ImageIcon className="size-4" />
            {t('blockChangePhotos')}
          </Button>
        ) : null}
      </div>

      {block.photos.length === 0 ? (
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="flex flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed border-line bg-surface px-4 py-8 text-center transition-colors hover:border-brand-300"
        >
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
            <ImageIcon className="size-5" />
          </span>
          <span className="text-sm font-medium text-ink">{t('blockChoosePhotos')}</span>
          <span className="text-xs text-ink-muted">{t('photoPickerHint', { max: count(L.photos, locale) })}</span>
        </button>
      ) : (
        <ol className="flex flex-col gap-3">
          {block.photos.map((photo, index) => {
            const image = byId.get(photo.imageId);
            return (
              <li key={photo.imageId} className="flex flex-col gap-3 rounded-control border border-line bg-surface p-3 sm:flex-row">
                <div
                  className={cx(
                    'relative shrink-0 self-start overflow-hidden rounded-control border border-line bg-surface-sunken',
                    wide ? 'aspect-[16/10] w-44' : 'aspect-[4/5] w-28',
                  )}
                >
                  {image ? (
                    <img
                      src={mediaUrl(image.url)}
                      alt={image.alt ?? ''}
                      // The close-up is the centre of the photograph, enlarged: how the page crops it.
                      className={cx('size-full object-cover transition-transform duration-300', photo.closeUp && 'scale-[2]')}
                    />
                  ) : (
                    <span className="flex size-full items-center justify-center text-ink-faint">
                      <ImageIcon className="size-6" />
                    </span>
                  )}
                  <span
                    data-numeric
                    className="absolute start-1.5 top-1.5 inline-flex size-5 items-center justify-center rounded-full bg-surface/90 text-[11px] font-medium text-ink shadow-card"
                  >
                    {count(index + 1, locale)}
                  </span>
                  {photo.closeUp ? (
                    <span className="absolute start-1.5 bottom-1.5 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-medium text-brand-ink">
                      {t('blockCloseUp')}
                    </span>
                  ) : null}
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  {image ? (
                    <>
                      <BilingualField
                        label={t('blockPhotoCaption')}
                        value={asPlain(photo.caption)}
                        limit={L.caption}
                        onChange={(caption) => setPhoto(index, { caption })}
                      />
                      <ToggleField
                        label={t('blockCloseUp')}
                        hint={t('blockCloseUpHint')}
                        checked={photo.closeUp}
                        onChange={(closeUp) => setPhoto(index, { closeUp })}
                      />
                    </>
                  ) : (
                    <Alert tone="info">{t('blockPhotoMissing')}</Alert>
                  )}
                </div>

                <div className="flex shrink-0 items-start gap-1 sm:flex-col">
                  <IconButton
                    label={t('moveEarlier')}
                    disabled={index === 0}
                    onClick={() => onChange({ ...block, photos: moveItem(block.photos, index, index - 1) })}
                    className="size-8 disabled:pointer-events-none disabled:opacity-35"
                  >
                    <ArrowLeftIcon className="size-4 rotate-90" />
                  </IconButton>
                  <IconButton
                    label={t('moveLater')}
                    disabled={index === block.photos.length - 1}
                    onClick={() => onChange({ ...block, photos: moveItem(block.photos, index, index + 1) })}
                    className="size-8 disabled:pointer-events-none disabled:opacity-35"
                  >
                    <ArrowLeftIcon className="size-4 -rotate-90" />
                  </IconButton>
                  <IconButton
                    label={t('remove')}
                    onClick={() => onChange({ ...block, photos: block.photos.filter((_, position) => position !== index) })}
                    className="size-8 hover:text-danger"
                  >
                    <TrashIcon className="size-4" />
                  </IconButton>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {picking ? (
        <PhotoPicker
          ctx={ctx}
          chosen={block.photos.map((photo) => photo.imageId)}
          onClose={() => setPicking(false)}
          onApply={(ids) => {
            // Photographs kept keep their caption and crop; new ones start plain.
            const kept = new Map(block.photos.map((photo) => [photo.imageId, photo]));
            onChange({ ...block, photos: ids.map((imageId) => kept.get(imageId) ?? { imageId, caption: emptyText(), closeUp: false }) });
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The product's photographs in a grid, picked in the order they will show —
 * each numbered as it is picked, and a colour's own photographs named by it,
 * so "the rose one, close" is found at a glance.
 */
function PhotoPicker({
  ctx,
  chosen,
  onClose,
  onApply,
}: {
  ctx: Context;
  chosen: string[];
  onClose: () => void;
  onApply: (imageIds: string[]) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [picked, setPicked] = useState(() => chosen.filter((id) => ctx.images.some((image) => image.id === id)));
  const fabrics = useMemo(() => (ctx.product ? fabricsOf(ctx.product).filter((fabric) => fabric.color !== null) : []), [ctx.product]);
  const full = picked.length >= L.photos;

  return (
    <Sheet
      open
      size="lg"
      title={t('blockChoosePhotos')}
      subtitle={t('photoPickerHint', { max: count(L.photos, locale) })}
      onClose={onClose}
      footer={
        <>
          <span data-numeric className="me-auto text-sm text-ink-muted">
            {t('photoPickerCount', { count: count(picked.length, locale), max: count(L.photos, locale) })}
          </span>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button onClick={() => onApply(picked)}>{t('photoPickerApply')}</Button>
        </>
      }
    >
      {full ? (
        <p className="mb-3 text-xs text-ink-muted">{t('photoPickerFull')}</p>
      ) : null}
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {ctx.images.map((image) => {
          const order = picked.indexOf(image.id);
          const selected = order >= 0;
          const fabric = fabricOfImage(fabrics, image);
          return (
            <li key={image.id}>
              <button
                type="button"
                aria-pressed={selected}
                disabled={!selected && full}
                onClick={() => setPicked((current) => (selected ? current.filter((id) => id !== image.id) : [...current, image.id]))}
                className={cx(
                  'relative block aspect-square w-full overflow-hidden rounded-control border-2 bg-surface-sunken transition-all disabled:cursor-not-allowed disabled:opacity-40',
                  selected ? 'border-brand-600 ring-2 ring-brand-300' : 'border-transparent hover:border-line',
                )}
              >
                <img src={mediaUrl(image.url)} alt={image.alt ?? ''} className="size-full object-cover" />
                {selected ? (
                  <span
                    data-numeric
                    className="absolute start-2 top-2 inline-flex size-6 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-brand-ink shadow-card"
                  >
                    {count(order + 1, locale)}
                  </span>
                ) : null}
                {fabric ? (
                  <span className="absolute start-1.5 bottom-1.5 inline-flex max-w-[calc(100%-0.75rem)] items-center gap-1.5 rounded-full bg-surface/90 px-2 py-0.5 text-[11px] text-ink shadow-card">
                    {fabric.swatchHex ? (
                      <span aria-hidden className="size-3 shrink-0 rounded-full border border-line" style={{ backgroundColor: fabric.swatchHex }} />
                    ) : null}
                    <span className="truncate">{fabric.label}</span>
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}

// ─── Highlights ──────────────────────────────────────────────────────────────

function HighlightsBody({
  block,
  ctx,
  onChange,
}: {
  block: BlockOf<'highlights'>;
  ctx: Context;
  onChange: (next: BlockOf<'highlights'>) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const specs = ctx.specifications;
  const full = block.items.length >= L.highlights;

  const setItem = (index: number, next: ProductHighlightInput) =>
    onChange({ ...block, items: block.items.map((item, position) => (position === index ? next : item)) });

  const add = () => {
    const used = new Set(block.items.map((item) => item.attribute));
    const spec = specs.find((entry) => !used.has(entry.code));
    onChange({ ...block, items: [...block.items, spec ? newHighlight(spec.code, suggestIcon(spec.code)) : newHighlight()] });
  };

  return (
    <div className="flex flex-col gap-3">
      <ol className="flex flex-col gap-3">
        {block.items.map((item, index) => {
          const spec = item.attribute ? specs.find((entry) => entry.code === item.attribute) : undefined;
          const gone = item.attribute !== null && !spec;
          return (
            <li key={index} className="rounded-control border border-line bg-surface p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-ink-muted">{t('blockHighlightIcon')}</span>
                  <IconPicker value={item.icon} onChange={(icon) => setItem(index, { ...item, icon })} />
                </div>
                <label className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
                  <span className="text-xs text-ink-muted">
                    {t('blockHighlightN', { n: count(index + 1, locale) })} · {t('blockHighlightSource')}
                  </span>
                  <Select
                    value={item.attribute ?? ''}
                    onChange={(event) => {
                      const code = event.target.value || null;
                      // A card keeps what was written for it, so switching back and forth loses nothing.
                      setItem(index, { ...item, attribute: code, icon: item.icon ?? (code ? suggestIcon(code) : null) });
                    }}
                    className="w-full"
                  >
                    <option value="">{t('blockWrittenHere')}</option>
                    {specs.length > 0 || gone ? (
                      <optgroup label={t('blockFromSpec')}>
                        {specs.map((entry) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.label} — {entry.display}
                          </option>
                        ))}
                        {gone && item.attribute ? <option value={item.attribute}>{item.attribute}</option> : null}
                      </optgroup>
                    ) : null}
                  </Select>
                </label>
                <div className="flex items-center gap-1">
                  <IconButton
                    label={t('moveUp')}
                    disabled={index === 0}
                    onClick={() => onChange({ ...block, items: moveItem(block.items, index, index - 1) })}
                    className="size-10 disabled:pointer-events-none disabled:opacity-35"
                  >
                    <ArrowLeftIcon className="size-4 rotate-90" />
                  </IconButton>
                  <IconButton
                    label={t('moveDown')}
                    disabled={index === block.items.length - 1}
                    onClick={() => onChange({ ...block, items: moveItem(block.items, index, index + 1) })}
                    className="size-10 disabled:pointer-events-none disabled:opacity-35"
                  >
                    <ArrowLeftIcon className="size-4 -rotate-90" />
                  </IconButton>
                  <IconButton
                    label={t('remove')}
                    onClick={() => onChange({ ...block, items: block.items.filter((_, position) => position !== index) })}
                    className="size-10 hover:text-danger"
                  >
                    <TrashIcon className="size-4" />
                  </IconButton>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-3">
                {item.attribute ? (
                  <>
                    {spec ? (
                      <div className="rounded-control border border-line bg-surface-sunken px-3 py-2">
                        <p className="text-xs text-ink-muted">{t('blockSpecValue')}</p>
                        <p className="text-sm font-medium text-ink">{spec.display}</p>
                        <p className="mt-0.5 text-[11px] text-ink-faint">{t('blockSpecValueHint')}</p>
                      </div>
                    ) : (
                      <Alert tone="info">{t('blockSpecGone')}</Alert>
                    )}
                    <BilingualField
                      label={t('blockLabelOverride')}
                      hint={t('blockLabelOverrideHint')}
                      placeholder={spec?.label}
                      value={asPlain(item.label)}
                      limit={L.label}
                      onChange={(label) => setItem(index, { ...item, label })}
                    />
                  </>
                ) : (
                  <>
                    <BilingualField
                      label={t('blockHighlightLabel')}
                      value={asPlain(item.label)}
                      limit={L.label}
                      onChange={(label) => setItem(index, { ...item, label })}
                    />
                    <BilingualField
                      label={t('blockHighlightValue')}
                      value={asPlain(item.value)}
                      limit={L.value}
                      onChange={(value) => setItem(index, { ...item, value })}
                    />
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {specs.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-dashed border-line px-3 py-2">
          <p className="text-xs text-ink-muted">{ctx.product ? t('blockSpecsNone') : t('blockSpecsSaveFirst')}</p>
          {ctx.product ? (
            <Button variant="ghost" size="sm" onClick={() => ctx.onTab('specs')}>
              {t('goToSpecs')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" disabled={full} onClick={add}>
          <PlusIcon className="size-4" />
          {t('addHighlight')}
        </Button>
        {full ? <span className="text-xs text-ink-muted">{t('highlightsFull', { max: count(L.highlights, locale) })}</span> : null}
      </div>
    </div>
  );
}

/** The chosen icon as a button; a small grid of the set, and "no icon", under it. */
function IconPicker({ value, onChange }: { value: HighlightIcon | null; onChange: (icon: HighlightIcon | null) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useDismiss(open, root, () => setOpen(false));
  const name = (icon: HighlightIcon | null) => (icon ? t(`highlightIcon_${icon}`) : t('blockNoIcon'));

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('blockHighlightIcon')}: ${name(value)}`}
        title={name(value)}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex size-10 items-center justify-center rounded-control border border-line bg-surface text-ink transition-colors hover:bg-surface-sunken"
      >
        {value ? <HighlightGlyph icon={value} className="size-5" /> : <NoIcon />}
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label={t('blockHighlightIcon')}
          className="absolute start-0 top-full z-30 mt-1 grid w-72 grid-cols-4 gap-1 rounded-control border border-line bg-surface p-2 shadow-float"
        >
          {[null, ...HIGHLIGHT_ICONS].map((icon) => (
            <button
              key={icon ?? 'none'}
              type="button"
              role="option"
              aria-selected={icon === value}
              onClick={() => {
                onChange(icon);
                setOpen(false);
              }}
              className={cx(
                'flex min-w-0 flex-col items-center gap-1 rounded-control px-1 py-2 text-[11px] transition-colors',
                icon === value ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
              )}
            >
              {icon ? <HighlightGlyph icon={icon} className="size-5" /> : <NoIcon />}
              <span className="max-w-full truncate">{name(icon)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const NoIcon = () => <span aria-hidden className="inline-block size-5 rounded-full border border-dashed border-ink-faint" />;

// ─── List ────────────────────────────────────────────────────────────────────

function ListBody({ block, onChange }: { block: BlockOf<'list'>; onChange: (next: BlockOf<'list'>) => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const rows = useRef<HTMLOListElement>(null);
  // Where the caret goes after Enter made a point: the same language, one row down.
  const [focus, setFocus] = useState<{ index: number; lang: Language } | null>(null);
  const full = block.points.length >= L.points;

  useEffect(() => {
    if (!focus) return;
    rows.current?.querySelector<HTMLInputElement>(`[data-point="${focus.index}"] input[lang="${focus.lang}"]`)?.focus();
    setFocus(null);
  }, [focus]);

  const setPoint = (index: number, point: BilingualTextDto) =>
    onChange({ ...block, points: block.points.map((entry, position) => (position === index ? point : entry)) });
  const insertAfter = (index: number, lang: Language) => {
    if (full) return;
    const points = [...block.points];
    points.splice(index + 1, 0, emptyText());
    onChange({ ...block, points });
    setFocus({ index: index + 1, lang });
  };

  return (
    <div className="flex flex-col gap-4">
      <BilingualField
        label={t('blockHeading')}
        value={asPlain(block.heading)}
        limit={L.heading}
        onChange={(heading) => onChange({ ...block, heading })}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('blockListStyle')}</span>
        <div>
          <Segmented
            label={t('blockListStyle')}
            items={[
              { id: 'bulleted', label: t('listBulleted') },
              { id: 'numbered', label: t('listNumbered') },
            ]}
            value={block.style}
            onChange={(style) => onChange({ ...block, style })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">{t('blockPoints')}</span>
        <ol ref={rows} className="flex flex-col gap-2">
          {block.points.map((point, index) => (
            <li
              key={index}
              data-point={index}
              className="flex items-start gap-2"
              // Enter in a point starts the next one, as in any list editor, rather than saving the product.
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || event.nativeEvent.isComposing || !(event.target instanceof HTMLInputElement)) return;
                event.preventDefault();
                insertAfter(index, event.target.lang === 'ar' ? 'ar' : 'en');
              }}
            >
              <span data-numeric aria-hidden className="mt-2.5 w-6 shrink-0 text-center text-sm text-ink-muted">
                {block.style === 'numbered' ? `${count(index + 1, locale)}.` : '•'}
              </span>
              <div className="min-w-0 flex-1">
                <BilingualField
                  compact
                  label={t('blockPointN', { n: count(index + 1, locale) })}
                  value={asPlain(point)}
                  limit={L.point}
                  onChange={(next) => setPoint(index, next)}
                />
              </div>
              <div className="flex shrink-0 items-center">
                <IconButton
                  label={t('moveUp')}
                  disabled={index === 0}
                  onClick={() => onChange({ ...block, points: moveItem(block.points, index, index - 1) })}
                  className="size-10 border-0 disabled:pointer-events-none disabled:opacity-35"
                >
                  <ArrowLeftIcon className="size-4 rotate-90" />
                </IconButton>
                <IconButton
                  label={t('moveDown')}
                  disabled={index === block.points.length - 1}
                  onClick={() => onChange({ ...block, points: moveItem(block.points, index, index + 1) })}
                  className="size-10 border-0 disabled:pointer-events-none disabled:opacity-35"
                >
                  <ArrowLeftIcon className="size-4 -rotate-90" />
                </IconButton>
                <IconButton
                  label={t('remove')}
                  onClick={() => onChange({ ...block, points: block.points.filter((_, position) => position !== index) })}
                  className="size-10 border-0 hover:text-danger"
                >
                  <TrashIcon className="size-4" />
                </IconButton>
              </div>
            </li>
          ))}
        </ol>
        <p className="text-xs text-ink-faint">{t('blockPointsHint')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" size="sm" disabled={full} onClick={() => insertAfter(block.points.length - 1, locale)}>
            <PlusIcon className="size-4" />
            {t('addPoint')}
          </Button>
          {full ? <span className="text-xs text-ink-muted">{t('pointsFull', { max: count(L.points, locale) })}</span> : null}
        </div>
      </div>
    </div>
  );
}

// ─── Looks and categories ────────────────────────────────────────────────────

/** Blocks whose content is the product's own relations: only their heading is written here. */
function AutomaticBody({
  block,
  ctx,
  onChange,
}: {
  block: BlockOf<'looks'> | BlockOf<'categories'>;
  ctx: Context;
  onChange: (next: BlockOf<'looks'> | BlockOf<'categories'>) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-4">
      {block.type === 'looks' ? (
        <p className="text-sm text-ink-muted">{t('blockLooksShows')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-ink-muted">{ctx.alsoIn.length > 0 ? t('blockCategoriesShows') : t('blockCategoriesNone')}</p>
          {ctx.alsoIn.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {ctx.alsoIn.map((name) => (
                <li key={name} className="rounded-full border border-line bg-surface px-3 py-1 text-sm text-ink">
                  {name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <BilingualField
        label={t('blockHeading')}
        placeholder={t('blockHeadingTheme')}
        value={asPlain(block.heading)}
        limit={L.heading}
        onChange={(heading) => onChange({ ...block, heading })}
      />
    </div>
  );
}

// ─── Preview ─────────────────────────────────────────────────────────────────

const HIGHLIGHT_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
};

/**
 * The page as a shopper reads it, in either language: the lead, then the
 * blocks as saved — tidied, so an empty block is as absent here as on the
 * store. A piece missing in one language shows in the other, as a theme
 * falls back, and the count of those says what is left to translate.
 */
function DescriptionPreview({
  ctx,
  name,
  shortDescription,
  blocks,
  onClose,
}: {
  ctx: Context;
  name: BilingualValue;
  shortDescription: BilingualValue;
  blocks: ProductBlockInput[];
  onClose: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [language, setLanguage] = useState<Language>(locale);
  const tidy = useMemo(() => tidyProductBlocks(blocks) ?? [], [blocks]);
  const imageById = new Map(ctx.images.map((image) => [image.id, image]));
  const specByCode = new Map(ctx.specifications.map((spec) => [spec.code, spec]));

  // What rendering the page ran into, for the notes around it.
  const seen = { fallbacks: 0, specifications: false };
  const pick = (text: BilingualTextDto | PlainText | null | undefined): string | null => {
    const own = text?.[language]?.trim();
    if (own) return own;
    const other = text?.[language === 'ar' ? 'en' : 'ar']?.trim();
    if (other) seen.fallbacks += 1;
    return other || null;
  };
  const heading = (text: BilingualTextDto, themeHeading = false) => {
    const words = pick(text);
    if (words) {
      return (
        <h2 dir="auto" className="mb-3 text-lg font-semibold text-ink">
          {words}
        </h2>
      );
    }
    return themeHeading ? <h2 className="mb-3 text-lg font-semibold text-ink-faint italic">{t('previewThemeHeading')}</h2> : null;
  };

  const render = (block: ProductBlockInput): ReactNode => {
    switch (block.type) {
      case 'text': {
        const body = pick(block.body);
        return (
          <section key={block.id}>
            {heading(block.heading)}
            {body ? (
              <p dir="auto" className="text-[15px] leading-7 whitespace-pre-line text-ink">
                {body}
              </p>
            ) : null}
          </section>
        );
      }
      case 'photos': {
        const shown = block.photos.flatMap((photo) => {
          const image = imageById.get(photo.imageId);
          return image ? [{ photo, image }] : [];
        });
        if (shown.length === 0) return null;
        return (
          <section key={block.id} className={cx('grid gap-3', shown.length === 2 && 'grid-cols-2', shown.length === 3 && 'grid-cols-3')}>
            {shown.map(({ photo, image }) => {
              const caption = pick(photo.caption);
              return (
                <figure key={image.id} className="min-w-0">
                  <div className={cx('overflow-hidden rounded-card bg-surface-sunken', shown.length === 1 ? 'aspect-[16/10]' : 'aspect-[4/5]')}>
                    <img src={mediaUrl(image.url)} alt={image.alt ?? ''} className={cx('size-full object-cover', photo.closeUp && 'scale-[2]')} />
                  </div>
                  {caption ? (
                    <figcaption dir="auto" className="mt-2 text-xs text-ink-muted">
                      {caption}
                    </figcaption>
                  ) : null}
                </figure>
              );
            })}
          </section>
        );
      }
      case 'highlights': {
        const cards = block.items.flatMap((item) => {
          if (item.attribute) {
            // Read from the answer, as the store does; one no longer answered drops out.
            const spec = specByCode.get(item.attribute);
            if (!spec) return [];
            seen.specifications = true;
            return [{ icon: item.icon, label: pick(item.label) ?? spec.label, value: spec.display }];
          }
          const value = pick(item.value);
          return value ? [{ icon: item.icon, label: pick(item.label), value }] : [];
        });
        if (cards.length === 0) return null;
        return (
          <section key={block.id} className={cx('grid gap-3', HIGHLIGHT_COLUMNS[cards.length])}>
            {cards.map((card, index) => (
              <div key={index} className="flex flex-col gap-2 rounded-card border border-line bg-surface-sunken/60 p-4">
                {card.icon ? <HighlightGlyph icon={card.icon} className="size-6 text-brand-600" /> : null}
                {card.label ? (
                  <p dir="auto" className="text-xs text-ink-muted">
                    {card.label}
                  </p>
                ) : null}
                <p dir="auto" className="text-sm font-medium text-ink">
                  {card.value}
                </p>
              </div>
            ))}
          </section>
        );
      }
      case 'list': {
        const points = block.points.map((point) => pick(point)).filter((point): point is string => point !== null);
        if (points.length === 0) return null;
        const List = block.style === 'numbered' ? 'ol' : 'ul';
        return (
          <section key={block.id}>
            {heading(block.heading)}
            <List
              className={cx(
                'flex flex-col gap-1.5 ps-5 text-[15px] leading-7 text-ink',
                block.style === 'numbered' ? 'list-decimal' : 'list-disc',
              )}
            >
              {points.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </List>
          </section>
        );
      }
      case 'looks':
        return (
          <section key={block.id}>
            {heading(block.heading, true)}
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((slot) => (
                <div key={slot} className="aspect-[3/4] rounded-card border border-dashed border-line bg-surface-sunken" />
              ))}
            </div>
            <p className="mt-2 text-xs text-ink-faint">{t('previewLooks')}</p>
          </section>
        );
      case 'categories':
        return (
          <section key={block.id}>
            {heading(block.heading, true)}
            {ctx.alsoIn.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {ctx.alsoIn.map((category) => (
                  <li key={category} className="rounded-full border border-line px-3 py-1 text-sm text-ink">
                    {category}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-ink-faint">{t('blockCategoriesNone')}</p>
            )}
          </section>
        );
      default:
        return null;
    }
  };

  // Rendered before the header reads the counts they add to.
  const title = pick(name);
  const lead = pick(shortDescription);
  const body = tidy.map(render).filter((node) => node !== null);

  return (
    <Sheet open size="xl" title={t('descPreview')} subtitle={t('previewSubtitle')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmented
            label={t('descPreview')}
            items={[
              { id: 'ar', label: t('langNameAr') },
              { id: 'en', label: t('langNameEn') },
            ]}
            value={language}
            onChange={setLanguage}
          />
          {seen.fallbacks > 0 ? (
            <span data-numeric className="text-xs text-warning">
              {t('previewFallback', { count: count(seen.fallbacks, locale) })}
            </span>
          ) : null}
        </div>

        <article
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          lang={language}
          className="rounded-card border border-line bg-surface px-5 py-8 sm:px-10"
        >
          <div className="mx-auto flex max-w-2xl flex-col gap-10">
            <header>
              {title ? (
                <h1 dir="auto" className="text-2xl font-semibold tracking-tight text-ink">
                  {title}
                </h1>
              ) : null}
              {lead ? (
                <p dir="auto" className="mt-3 font-serif text-xl leading-relaxed text-ink-muted">
                  {lead}
                </p>
              ) : null}
            </header>
            {body.length > 0 ? body : <p className="text-center text-sm text-ink-faint">{t('previewEmpty')}</p>}
          </div>
        </article>

        {seen.specifications ? <p className="text-xs text-ink-muted">{t('previewSpecNote')}</p> : null}
      </div>
    </Sheet>
  );
}
